import { inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Observable, map, of, tap } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { ACCESS_TOKEN_PROVIDER } from '../access-token.provider';

import { UserInfoService } from './user-info.service';
import { UserInfo } from './user-info.types';

export class AppServiceUserInfoService extends UserInfoService {
    #logger = inject(Logger).createCustomLogger('UserInfoService');
    #accessToken: Observable<string | undefined> =
        inject(ACCESS_TOKEN_PROVIDER, { optional: true })?.accessTokenOnce$ ??
        of(undefined).pipe(
            tap(() => this.#logger.error('ACCESS_TOKEN_PROVIDER is not defined: No access token available.'))
        );
    readonly loggedInUser: Observable<UserInfo> = this.#accessToken.pipe(
        map((accessToken?: string) => this.extractUserinfo(accessToken))
    );

    /** Because of impersonation, one cannot ask BPS to get the current user, we must take it from the token. */
    private extractUserinfo(accessToken: string | undefined): UserInfo {
        if (!accessToken) {
            this.#logger.info('No access token.');
            return {} as UserInfo;
        }
        try {
            const token = jwtDecode<AccessToken>(accessToken);
            if (!token.upn || !token.name) {
                this.#logger.warn(`No upn or name found for logged in user ${JSON.stringify(token)}`);
            }

            return {
                // user id is only used to provide a value to the 'isLoggedInUser' user.
                // It could be a string const as well.
                // The sub is only a hack for b2b azure entra impersonation support.
                userId: token.upn ?? token.sub,
                email: token.upn,
                fullname: token.name
            };
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (_) {
            return {} as UserInfo;
        }
    }
}

interface AccessToken {
    aud: string;
    iss: string;
    iat: number;
    nbf: number;
    exp: number;
    acr: string;
    aio: string;
    amr: string[];
    appid: string;
    appidacr: string;
    deviceid: string;
    family_name: string;
    given_name: string;
    ipaddr: string;
    name: string;
    oid: string;
    onprem_sid: string;
    rh: string;
    scp: string;
    sid: string;
    sub: string;
    tid: string;
    unique_name: string;
    upn: string;
    uti: string;
    ver: string;
    xms_ftd: string;
}
